import { useConfigStore } from "@/stores/widget-store";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import AccessibilityCheckerIcon from "@/icons/AccessibilityCheckerIcon";
import { BrowserPreview } from "../preview/BrowserPreview";

interface AccessibilityCheckerTabProps {
    onSave: () => Promise<void>;
}

export const AccessibilityCheckerTab = ({ onSave }: AccessibilityCheckerTabProps) => {
    const { config, setChecker, isLoading, error } = useConfigStore();
    
    // Master toggle controls if fields are disabled
    const isDisabled = !config.checker?.enabled;
    
    return (
      <Card
        className="cy:rounded-lg cy:shadow-none! cy:w-full cy:sm:p-8"
        style={{
          background: "#FFFFFF",
          border: "1px solid #CACEE4",
          borderRadius: "12px",
          gap: "8px",
        }}
      >
        {/* HEADER - Title with master toggle */}
        <CardHeader className="cy:border-b cy:border-gray-200 cy:pb-2 cy:px-0">
          <div className="cy:flex cy:items-center cy:gap-4 cy:mb-2">
            <CardTitle className="cy:text-xl cy:font-semibold">Scanner</CardTitle>
            <Switch
              checked={config.checker?.enabled || false}
              onCheckedChange={(checked) => setChecker('enabled', checked)}
            />
          </div>
          <CardDescription className="cy:text-sm cy:text-gray-600 cy:pb-6">
          Scan accessibility issues directly from the front end of your website. The scanner widget will not be visible to your end users.          </CardDescription>
        </CardHeader>
        
        <CardContent className="cy:px-0 cy:pt-6">
          <div className="cy:space-y-8">
            {/* Checker Position Section */}
            <div className="cy:p-4 cy:border cy:border-gray-200">
              <div className="cy:grid cy:grid-cols-2 cy:gap-8">
                {/* Left: Position Controls */}
                <div className="cy:space-y-4">
                  <h4 className={`cy:text-base cy:font-semibold cy:mb-2 ${
                    isDisabled ? "cy:text-gray-400" : ""
                  }`}>
                    Checker position
                  </h4>
                  
                  <p className={`cy:text-sm cy:text-gray-600 cy:pb-2 cy:mb-6 ${
                    isDisabled ? "cy:text-gray-400" : ""
                  }`}>
                    Set the on-screen position of the Accessibility checker for better visibility.
                  </p>
                  
                  <Label
                    className={`cy:block cy:mb-3 cy:text-sm ${
                      isDisabled ? "cy:text-gray-400" : ""
                    }`}
                  >
                    Position
                  </Label>
                  
                  <div className="cy:flex cy:gap-3">
                    <label
                      className={`cy:flex-1 cy:inline-flex cy:items-center cy:justify-start cy:gap-2 cy:px-3 cy:pt-2 cy:pb-1 cy:rounded cy:border-2 cy:cursor-pointer cy:transition-all ${
                        config.checker?.placement?.position === "left"
                          ? "cy:border-blue-600 cy:bg-white"
                          : "cy:border-gray-300 cy:bg-white"
                      } ${
                        isDisabled ? "cy:opacity-50 cy:cursor-not-allowed" : "cy:hover:border-blue-400"
                      }`}
                    >
                      <input
                        type="radio"
                        name="checker-position"
                        value="left"
                        checked={config.checker?.placement?.position === "left"}
                        onChange={() => setChecker("placement.position", "left")}
                        disabled={isDisabled}
                        className="cy:w-4 cy:h-4 cy:text-blue-600 focus:cy:ring-blue-500 cy:flex-shrink-0"
                      />
                      <span className="cy:text-[13px] cy:font-bold cy:text-gray-700 cy:leading-[16px] cy:pb-1">Left</span>
                    </label>
                    
                    <label
                      className={`cy:flex-1 cy:inline-flex cy:items-center cy:justify-start cy:gap-2 cy:px-3 cy:pt-2 cy:pb-1 cy:rounded cy:border-2 cy:cursor-pointer cy:transition-all ${
                        config.checker?.placement?.position === "right"
                          ? "cy:border-blue-600 cy:bg-white"
                          : "cy:border-gray-300 cy:bg-white"
                      } ${
                        isDisabled ? "cy:opacity-50 cy:cursor-not-allowed" : "cy:hover:border-blue-400"
                      }`}
                    >
                      <input
                        type="radio"
                        name="checker-position"
                        value="right"
                        checked={config.checker?.placement?.position === "right"}
                        onChange={() => setChecker("placement.position", "right")}
                        disabled={isDisabled}
                        className="cy:w-4 cy:h-4 cy:text-blue-600 focus:cy:ring-blue-500 cy:flex-shrink-0"
                      />
                      <span className="cy:text-[13px] cy:font-bold cy:text-gray-700 cy:leading-[16px] cy:pb-1">Right</span>
                    </label>
                  </div>
                </div>

                {/* Right: Preview */}
                <div className="cy:relative cy:flex cy:items-center cy:justify-center">
                  <div 
                    className={`cy:relative cy:w-[381px] cy:h-[220px] cy:transition-opacity cy:duration-200 ${
                      isDisabled ? "cy:opacity-40 cy:grayscale cy:pointer-events-none" : ""
                    }`}
                  >
                    {/* Browser Preview Background */}
                    <BrowserPreview />

                    {/* Scanner Icon - Positioned dynamically */}
                    <div
                      className="cy:absolute cy:bottom-4 cy:transition-all cy:duration-300"
                      style={{
                        [config.checker?.placement?.position === 'left' ? 'left' : 'right']: '10px',
                      }}
                    >
                      <AccessibilityCheckerIcon width={60} height={60} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="cy:pt-4">
              {error && (
                <div className="cy:text-red-500 cy:mb-2 cy:text-sm">{error}</div>
              )}
              <Button
                onClick={onSave}
                disabled={isLoading}
                className="cy:rounded! cy:font-semibold!"
              >
                {isLoading ? "Saving..." : "Save settings"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };